import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingContact from '@/components/FloatingContact';
import SEOHead from '@/components/SEOHead';
import { trackFormSubmit } from '@/utils/analytics';

const ForBuildersPage = () => {
  const { lang, getLocalized, getLocalizedPath } = useLanguage();
  const [page, setPage] = useState(null);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', type: 'Builder / Contractor', message: '' });
  const [formStatus, setFormStatus] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, productsRes, settingsRes] = await Promise.all([
          api.get('/pages/for-builders'),
          api.get('/products'),
          api.get('/site-settings')
        ]);
        setPage(pageRes.data);
        setProducts(productsRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Error:', err);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  const loc = page ? getLocalized(page) : {};
  const contact = settings?.contact || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      await api.post('/leads', { ...formData, locale: lang, page: 'for-builders' });
      setFormStatus('success');
      setFormData({ first_name: '', last_name: '', email: '', phone: '', type: 'Builder / Contractor', message: '' });
    } catch (err) {
      setFormStatus('error');
    }
  };

  return (
    <div data-testid="builders-page">
      <SEOHead
        title={lang === 'es' ? 'Para Constructores y Contratistas' : 'For Builders & Contractors'}
        description={lang === 'es' ? 'Su socio confiable en fenestraci00f3n. Suministro consistente, precios por volumen y soporte t\u00e9cnico para constructores en Texas.' : 'Your reliable fenestration partner. Consistent supply, volume pricing, and technical support for builders in Texas.'}
        path="/for-builders"
      />
      <Navbar />
      <section className="bl-subpage-hero">
        <div className="bl-subpage-hero-bg" style={{ backgroundImage: `url(${loc.hero_image || ''})` }} />
        <div className="bl-subpage-hero-content">
          <div className="eyebrow">{lang === 'es' ? 'Para Profesionales' : 'For Professionals'}</div>
          <h1>{loc.title || 'For Builders'}</h1>
          <p>{loc.subtitle || ''}</p>
        </div>
      </section>

      <div className="bl-info-content">
        <p>{loc.content || ''}</p>
      </div>

      {loc.advantages && loc.advantages.length > 0 && (
        <div className="bl-advantages-grid">
          {loc.advantages.map((a, i) => (
            <div className="bl-advantage" key={i}>
              <h3>{a.title}</h3>
              <p>{a.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Builder-specific form */}
      <div style={{ padding: '6rem 5vw', background: 'var(--white)' }}>
        <div style={{ maxWidth: '600px' }}>
          <div className="bl-section-eyebrow">{lang === 'es' ? 'Comienza' : 'Get Started'}</div>
          <div className="bl-section-title" style={{ marginBottom: '2rem' }}>{lang === 'es' ? 'Solicita Informaci\u00f3n' : 'Request Information'}</div>
          {formStatus === 'success' ? (
            <div className="bl-form-success">
              {lang === 'es' ? 'Mensaje enviado. Te contactaremos pronto.' : 'Message sent! We\'ll be in touch soon.'}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="bl-form-row">
                <div className="bl-form-field">
                  <label>{lang === 'es' ? 'Nombre' : 'First Name'}</label>
                  <input type="text" value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required />
                </div>
                <div className="bl-form-field">
                  <label>{lang === 'es' ? 'Apellido' : 'Last Name'}</label>
                  <input type="text" value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required />
                </div>
              </div>
              <div className="bl-form-field">
                <label>Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
              </div>
              <div className="bl-form-field">
                <label>{lang === 'es' ? 'Tel\u00e9fono' : 'Phone'}</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              </div>
              <div className="bl-form-field">
                <label>{lang === 'es' ? 'Detalles del Proyecto' : 'Project Details'}</label>
                <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder={lang === 'es' ? 'Cu\u00e9ntanos sobre tus necesidades...' : 'Tell us about your project needs...'} />
              </div>
              <button type="submit" className="bl-form-submit" disabled={formStatus === 'sending'}>
                {formStatus === 'sending' ? '...' : (lang === 'es' ? 'Enviar Solicitud \u2192' : 'Submit Request \u2192')}
              </button>
            </form>
          )}
        </div>
      </div>

      <Footer products={products} settings={settings} />
      <FloatingContact settings={settings} />
    </div>
  );
};

export default ForBuildersPage;
